import { FC } from 'react';
import { useHistory } from 'react-router';
import { useQuery } from '@apollo/client';
import { Divider, Flex, Image } from '@chakra-ui/react';

import okHandImg from 'assets/brand/ok-hand.png';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineLoader from 'components/_core/SunshineLoader';
import Text from 'components/_core/Text';
import ErrorView from 'components/ErrorView';
import TrackView from 'features/Analytics/TrackView';
import * as routes from 'features/Bank/routes';
import * as Layout from 'layouts/StepperLayout';

import { BankAccountLimitsForSuccessPageDocument } from './graphql/bankAccountLimitsForSuccessPage.gql';
import locales from './locales';

interface UpgradeFromBankAccountsPageSuccessProps {
  planName: string;
  companyProfileId: string;
}

const UpgradeFromBankAccountsPageSuccess: FC<
  UpgradeFromBankAccountsPageSuccessProps
> = ({ companyProfileId, planName }) => {
  const history = useHistory();
  const { data, error, loading } = useQuery(
    BankAccountLimitsForSuccessPageDocument,
    {
      skip: !companyProfileId,
      variables: { companyProfileId: companyProfileId as string },
    },
  );

  if (loading || !data) {
    return <SunshineLoader />;
  }

  if (error) {
    return <ErrorView />;
  }

  const nbWallets =
    data.viewer.company.currentPlan.limits.activeBankAccounts.value - 1;

  const onClick = () => {
    history.push({
      pathname: routes.newBankAccount,
      state: {
        returnPath: routes.bankAccounts,
      },
    });
  };

  return (
    <Layout.Root>
      <Layout.Body>
        <SunshineCard padding="space-0" width={600}>
          <TrackView pageName="UpgradeFromBankAccountsPageSuccess" />
          <Flex direction="column" paddingTop="space-40">
            <Image height={180} objectFit="contain" src={okHandImg} />

            <Heading
              marginTop="space-24"
              paddingX="space-32"
              size="lg"
              textAlign="center"
            >
              {locales.formatString(locales.title, { planName })}
            </Heading>

            <Text marginTop="space-16" paddingX="space-32" textAlign="center">
              {locales.formatString(
                nbWallets > 1
                  ? locales.description.plural
                  : locales.description.singular,
                { nbWallets },
              )}
            </Text>

            <Divider marginTop="space-40" />

            <Flex align="center" direction="column" padding="space-24">
              <Button
                icon="arrow-right"
                isExpanded
                marginBottom="space-0"
                onClick={onClick}
                textAlign="center"
              >
                {locales.button}
              </Button>
            </Flex>
          </Flex>
        </SunshineCard>
      </Layout.Body>
    </Layout.Root>
  );
};

export default UpgradeFromBankAccountsPageSuccess;
